import { useEffect } from 'react';

import type { ColorSchemeKey } from '@/lib/colorSchemes';
import { COLOR_SCHEMES } from '@/lib/colorSchemes';
import { useChartStore } from '@/store/chartStore';
import type { ChartDefaults } from '@/types/chart';

/**
 * Hook to apply chart-specific defaults and reset on unmount
 *
 * Usage:
 * ```tsx
 * // Inline defaults
 * useChartDefaults({
 *   colorScheme: 'CATEGORICAL_5',
 *   showValues: true,
 * });
 *
 * // From chart data
 * useChartDefaults(chartData?.defaults);
 * ```
 */
export function useChartDefaults(defaults: ChartDefaults | undefined) {
  const setColorScheme = useChartStore((state) => state.setColorScheme);
  const setUseLogScale = useChartStore((state) => state.setUseLogScale);
  const setShowValues = useChartStore((state) => state.setShowValues);
  const setShowLegend = useChartStore((state) => state.setShowLegend);
  const setMaxHeight = useChartStore((state) => state.setMaxHeight);
  const setSegmentLayout = useChartStore((state) => state.setSegmentLayout);
  const setSegmentGap = useChartStore((state) => state.setSegmentGap);
  const resetConfig = useChartStore((state) => state.resetConfig);

  // Serialize defaults for stable dependency comparison
  const serializedDefaults = defaults ? JSON.stringify(defaults) : null;

  // Apply defaults on mount and when they change
  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to run on mount and when defaults change
  useEffect(() => {
    if (!defaults) return;

    // Handle colorScheme - can be ColorSchemeKey or resolved ColorScheme object
    if (defaults.colorScheme !== undefined) {
      if (typeof defaults.colorScheme === 'string') {
        const schemeKey = defaults.colorScheme as ColorSchemeKey;
        const scheme = COLOR_SCHEMES[schemeKey];
        if (scheme) {
          setColorScheme(scheme, schemeKey);
        }
      } else {
        setColorScheme(defaults.colorScheme);
      }
    }
    if (defaults.useLogScale !== undefined) {
      setUseLogScale(defaults.useLogScale);
    }
    if (defaults.showValues !== undefined) {
      setShowValues(defaults.showValues);
    }
    if (defaults.showLegend !== undefined) {
      setShowLegend(defaults.showLegend);
    }
    if (defaults.maxHeight !== undefined) {
      setMaxHeight(defaults.maxHeight);
    }
    if (defaults.segmentLayout !== undefined) {
      setSegmentLayout(defaults.segmentLayout);
    }
    if (defaults.segmentGap !== undefined) {
      setSegmentGap(defaults.segmentGap);
    }

    // Reset to global defaults on unmount or when defaults change
    return () => {
      resetConfig();
    };
    // Use serialized string for stable comparison of object literals
  }, [serializedDefaults]);
}
